import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useCampaignMetrics = (dateRange) => {
  return useQuery({
    queryKey: ['campaign-metrics', dateRange],
    queryFn: async () => {
      // Busca campanhas
      const { data: campaigns, error: campaignsError } = await supabase
        .from('ad_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;

      // Para cada campanha, busca as métricas agregadas
      const campaignsWithMetrics = await Promise.all(
        campaigns.map(async (campaign) => {
          let query = supabase
            .from('fb_ad_metrics')
            .select('*')
            .eq('campaign_id', campaign.id);

          // Aplica filtro de data se fornecido
          if (dateRange?.from) {
            query = query.gte('date', dateRange.from.toISOString().split('T')[0]);
          }
          if (dateRange?.to) {
            query = query.lte('date', dateRange.to.toISOString().split('T')[0]);
          }

          const { data: metrics, error: metricsError } = await query;

          if (metricsError) {
            console.error('Error fetching metrics for campaign', campaign.id, metricsError);
            return {
              ...campaign,
              metrics: {
                impressions: 0,
                clicks: 0,
                spend: 0,
                leads: 0,
                cpl: 0,
                ctr: 0,
              }
            };
          }

          // Agrega as métricas
          const aggregated = metrics.reduce((acc, m) => ({
            impressions: acc.impressions + (m.impressions || 0),
            reach: acc.reach + (m.reach || 0),
            clicks: acc.clicks + (m.clicks || 0),
            spend: acc.spend + (m.spend || 0),
            leads: acc.leads + (m.leads || 0),
            purchases: acc.purchases + (m.purchases || 0),
            video_views: acc.video_views + (m.video_views || 0),
            link_clicks: acc.link_clicks + (m.link_clicks || 0),
          }), {
            impressions: 0,
            reach: 0,
            clicks: 0,
            spend: 0,
            leads: 0,
            purchases: 0,
            video_views: 0,
            link_clicks: 0,
          });

          // Calcula métricas derivadas
          const cpl = aggregated.leads > 0 ? aggregated.spend / aggregated.leads : 0;
          const ctr = aggregated.impressions > 0 ? (aggregated.clicks / aggregated.impressions) * 100 : 0;
          const cpm = aggregated.impressions > 0 ? (aggregated.spend / aggregated.impressions) * 1000 : 0;

          return {
            ...campaign,
            metrics: {
              ...aggregated,
              cpl,
              ctr,
              cpm,
            }
          };
        })
      );

      // Ordena por gasto (maiores primeiro)
      return campaignsWithMetrics.sort((a, b) => 
        (b.metrics?.spend || 0) - (a.metrics?.spend || 0)
      );
    },
    enabled: true,
  });
};
